/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React from "react";

import Link from "next/link";
import styled from "styled-components";

import { Plate } from "src/classes/plate/Plate";
import { BaseUser } from "src/classes/user";
import { Avatar } from "src/components/Shared/User/Avatar";
import { Username } from "src/components/Shared/User/Username";
import { StyledLink } from "src/components/UI/Links";

interface Props {
  user: BaseUser;
  addable: boolean;
  plate: Plate;
}

export const SharedUser = ({ user, addable, plate }: Props) => {
  const userIsOwner = plate.owner.snapshot.id === user.snapshot.id;

  const handleRemove = () => {
    if (!userIsOwner) {
      plate.removeEditor(user);
    }
  };

  const handleAdd = () => {
    plate.addEditor(user);
  };

  return (
    <StyledUser>
      <UserDisplay>
        <Link passHref href={user.inAppLink}>
          <StyledLink>
            <Avatar src={user.avatarURL(42)} size={42} />
          </StyledLink>
        </Link>
        <Name>
          <Link passHref href={`/user/${user.snapshot.username}`}>
            <StyledLink href={`/user/${user.snapshot.username}`}>
              <DisplayName>{user.snapshot.displayName}</DisplayName>
            </StyledLink>
          </Link>
          <Username user={user} />
        </Name>
      </UserDisplay>
      <Role owner={userIsOwner} onClick={addable ? handleAdd : handleRemove}>
        {!addable ? (userIsOwner ? "Owner" : "Remove") : "Make Editor"}
      </Role>
    </StyledUser>
  );
};

const StyledUser = styled.div`
  font-family: "Inter";
  display: flex;
  flex: 1;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 12px 0;
  font-weight: 300;
  user-select: none;
`;

const UserDisplay = styled.div`
  display: flex;
  align-items: center;
`;

const Name = styled.div`
  margin-left: 6px;
  display: flex;
  justify-content: space-around;
  flex-direction: column;
  height: 40px;
`;

const DisplayName = styled.div`
  color: ${({ theme }) => theme.text.default};
  font-size: 14px;
`;

const Role = styled.div<{ owner: boolean }>`
  cursor: ${({ owner }) => (owner ? "default" : "pointer")};
  color: ${({ theme }) => theme.text.subtle};
  font-size: 13px;
  text-decoration: ${({ owner }) => (owner ? "none" : "underline")};
  transition: all 0.1s;
  &:hover {
    color: ${({ owner, theme }) => (owner ? theme.text.subtle : theme.text.default)};
  }
`;
